import {
  Bold,
  Code2,
  Heading2,
  Heading3,
  Italic,
  Link,
  List,
  ListOrdered,
  Quote,
  Strikethrough,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

type WikiTextToolbarProps = {
  value: string;
  onChange: (value: string) => void;
  getTextarea: () => HTMLTextAreaElement | null;
};

export function WikiTextToolbar({ value, onChange, getTextarea }: WikiTextToolbarProps) {
  function restoreSelection(start: number, end: number) {
    window.requestAnimationFrame(() => {
      const textarea = getTextarea();
      if (!textarea) return;
      textarea.focus();
      textarea.setSelectionRange(start, end);
    });
  }

  function wrapSelection(before: string, after: string, placeholder: string) {
    const textarea = getTextarea();
    const start = textarea?.selectionStart ?? value.length;
    const end = textarea?.selectionEnd ?? value.length;
    const selected = value.slice(start, end) || placeholder;
    onChange(`${value.slice(0, start)}${before}${selected}${after}${value.slice(end)}`);
    restoreSelection(start + before.length, start + before.length + selected.length);
  }

  function prefixLines(prefix: string | ((index: number) => string)) {
    const textarea = getTextarea();
    const selectionStart = textarea?.selectionStart ?? value.length;
    const selectionEnd = textarea?.selectionEnd ?? value.length;
    const lineStart = value.lastIndexOf('\n', Math.max(0, selectionStart - 1)) + 1;
    const nextLine = value.indexOf('\n', selectionEnd);
    const lineEnd = nextLine === -1 ? value.length : nextLine;
    const selectedLines = value.slice(lineStart, lineEnd).split('\n');
    const formatted = selectedLines
      .map((line, index) => `${typeof prefix === 'function' ? prefix(index) : prefix}${line}`)
      .join('\n');
    onChange(`${value.slice(0, lineStart)}${formatted}${value.slice(lineEnd)}`);
    restoreSelection(lineStart, lineStart + formatted.length);
  }

  function insertLink() {
    const textarea = getTextarea();
    const start = textarea?.selectionStart ?? value.length;
    const end = textarea?.selectionEnd ?? value.length;
    const selected = value.slice(start, end) || 'текст ссылки';
    const url = window.prompt('Адрес ссылки (https://… или /wiki/…)', 'https://');
    if (!url?.trim()) return;
    const inserted = `[${selected}](${url})`;
    onChange(`${value.slice(0, start)}${inserted}${value.slice(end)}`);
    const urlStart = start + selected.length + 3;
    restoreSelection(urlStart, urlStart + url.length);
  }

  const buttonClass = 'h-8 w-8';

  return (
    <div className="mb-2 flex flex-wrap gap-1 rounded-md border border-white/10 bg-eol-black/40 p-1.5" onMouseDown={(event) => event.preventDefault()}>
      <Button
        type="button"
        size="icon"
        variant="secondary"
        className={buttonClass}
        onClick={() => wrapSelection('**', '**', 'жирный текст')}
        title="Жирный"
        aria-label="Жирный"
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="secondary"
        className={buttonClass}
        onClick={() => wrapSelection('*', '*', 'курсив')}
        title="Курсив"
        aria-label="Курсив"
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="secondary"
        className={buttonClass}
        onClick={() => wrapSelection('~~', '~~', 'зачёркнутый текст')}
        title="Зачёркнутый"
        aria-label="Зачёркнутый"
      >
        <Strikethrough className="h-4 w-4" />
      </Button>
      <span className="mx-1 w-px bg-white/10" />
      <Button
        type="button"
        size="icon"
        variant="secondary"
        className={buttonClass}
        onClick={() => prefixLines('## ')}
        title="Заголовок"
        aria-label="Заголовок второго уровня"
      >
        <Heading2 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="secondary"
        className={buttonClass}
        onClick={() => prefixLines('### ')}
        title="Подзаголовок"
        aria-label="Заголовок третьего уровня"
      >
        <Heading3 className="h-4 w-4" />
      </Button>
      <span className="mx-1 w-px bg-white/10" />
      <Button
        type="button"
        size="icon"
        variant="secondary"
        className={buttonClass}
        onClick={() => prefixLines('- ')}
        title="Маркированный список"
        aria-label="Маркированный список"
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="secondary"
        className={buttonClass}
        onClick={() => prefixLines((index) => `${index + 1}. `)}
        title="Нумерованный список"
        aria-label="Нумерованный список"
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="secondary"
        className={buttonClass}
        onClick={() => prefixLines('> ')}
        title="Цитата"
        aria-label="Цитата"
      >
        <Quote className="h-4 w-4" />
      </Button>
      <span className="mx-1 w-px bg-white/10" />
      <Button
        type="button"
        size="icon"
        variant="secondary"
        className={buttonClass}
        onClick={insertLink}
        title="Ссылка"
        aria-label="Ссылка"
      >
        <Link className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="secondary"
        className={buttonClass}
        onClick={() => wrapSelection('`', '`', 'код')}
        title="Код"
        aria-label="Код"
      >
        <Code2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
